<template>

  <div id="fa-card-scanner" :class="{
    portrait: isPortrait,
    landscape: !isPortrait,
    'video-ready': videoReady
  }">
    <canvas
      :width="cardOverlayDimensions.width"
      :height="cardOverlayDimensions.height"
      id="canvas-preview"
      ref="previewCanvas"></canvas>

    <template v-if="!hasError">
      <template v-if="!previewImg">
        <video
          controlslist="nofullscreen nodownload noremoteplayback noplaybackrate"
          poster="data:image/gif,AAAA"
          muted
          playsinline
          autoplay
          :style="videoStyles"
          ref="video"
          @click.capture="capturePreview()"></video>

        <div id="backdrop" :style="backdropStyles" @click.capture="capturePreview()"></div>
        <div id="card-overlay" :style="cardOverlayStyles" @click.capture="capturePreview()">
          <div
            class="overlay photo-overlay"
            :style="photoOverlayStyles"
            @click.capture="capturePreview()">
            <!-- <img :src="faCardAvatarPlaceholder" /> -->
          </div>
          <div
            class="overlay name-overlay"
            :style="nameOverlayStyles"
            @click.capture="capturePreview()">
            <p class="overlay-hint below"><v-icon x-small color="rgb(0, 0, 255)">mdi-arrow-up-left</v-icon>Name</p>
          </div>
          <div
            class="overlay license-number-overlay"
            :style="licenseNumberOverlayStyles"
            @click.capture="capturePreview()">
            <p class="overlay-hint above">License Number<v-icon x-small color="rgb(0, 0, 255)">mdi-arrow-down-right</v-icon></p>
          </div>
        </div>

        <div class="hint" @click.capture="capturePreview()">
          <p>Align name and license number precisely within blue boxes. Ensure entire photo is visible.</p>
          <p>Click anywhere on the screen to capture the image.</p>
        </div>
      </template>

      <template v-else>
        <img id="img-preview" :src="previewImg" />
        <div class="btn-group">
          <v-btn plain x-large color="warning" elevation="4" @click="reset"><v-icon>mdi-camera-retake</v-icon> Retake</v-btn>
          <v-btn plain x-large color="primary" elevation="4" @click="acceptPreview"><v-icon>mdi-check-bold</v-icon> OK</v-btn>
        </div>
        <div class="hint">
          <p>Please make sure the captured image is in focus and the details are visible.</p>
        </div>
      </template>
    </template>

    <div v-else id="error">
      <v-alert dense outlined type="error">
        Error accessing camera. Please close the scanner and try again. If the problem persists, please reload this page.
      </v-alert>

      <v-btn text x-large outlined elevation="4" class="mt-4" @click="close()">Close</v-btn>
    </div>

    <v-btn icon large elevation="4" class="btn-close-float" color="warning" @click="close()"><v-icon>mdi-close</v-icon></v-btn>
  </div>

</template>

<script>
import faCardAvatarPlaceholder from '@/assets/img/fa-card-avatar-placeholder.svg'

export default {
  name: 'FaCardScanner',

  data () {
    return {
      initializing: false,
      videoReady: false,
      faCardAspectRatio: 0.675,
      overlayMaxWidth: 360,
      cameraStream: null,
      cameraSettings: null,
      videoDimensions: () => ({}),
      screenDimensions: () => ({}),
      hasError: false,
      resizeObserver: null,
      faCardAvatarPlaceholder,
      previewImg: null,
      previewImgWidth: 0,
      previewImgHeight: 0
    }
  },

  computed: {
    isPortrait () {
      return this.screenDimensions.isPortrait
    },

    isIOs () {
      const isIPhone = window.navigator.platform.toLowerCase() === 'iphone'
      const isIPad = window.navigator.platform.toLowerCase() === 'ipad' ||
        (window.navigator.platform.toLowerCase() === 'macintel' && window.navigator.standalone !== undefined)

      return isIPhone || isIPad
    },

    overlayScaleFactor () {
      return this.isPortrait ? 0.7 : 0.7
    },

    /**
     * Overlay dimensions are calculated programmatically so that they can be
     * applied both to the CSS and logically for extracting the image data.
     */
    cardOverlayDimensions () {
      const { width: screenWidth, height: screenHeight } = this.screenDimensions
      const { width: videoWidth, height: videoHeight } = this.videoDimensions

      if (!videoWidth) return { width: 0, height: 0, offsetX: 0, offsetY: 0 }

      let width, height

      if (this.isPortrait) {
        width = Math.min(
          screenWidth * this.overlayScaleFactor,
          videoWidth,
          videoHeight * this.faCardAspectRatio * 0.65
        )
        height = width / this.faCardAspectRatio
      } else {
        height = Math.min(
          screenHeight * this.overlayScaleFactor,
          videoHeight * 0.95 // make sure the overlay is shorter than the video
        )
        width = height * this.faCardAspectRatio
      }

      const offsetX = (screenWidth - width) / 2
      const offsetY = (screenHeight - height) / 2 - (this.isPortrait ? 0 : height / 10)

      return {
        width: Math.ceil(width),
        height: Math.ceil(height),
        offsetX: Math.floor(offsetX),
        offsetY: Math.floor(offsetY)
      }
    },

    photoOverlayDimensions () {
      const { width: maxWidth, height: maxHeight } = this.cardOverlayDimensions
      const width = 0.72 * maxWidth
      const height = 0.465 * maxHeight
      const left = (maxWidth - width) / 2
      const top = 0.21 * maxHeight

      return {
        width: Math.ceil(width),
        height: Math.ceil(height),
        left: Math.floor(left),
        top: Math.floor(top),
        widthRatio: width / maxWidth,
        heightRatio: height / maxHeight,
        topRatio: top / maxHeight,
        leftRatio: left / maxWidth
      }
    },

    nameOverlayDimensions () {
      const { height: maxHeight } = this.cardOverlayDimensions
      const photo = this.photoOverlayDimensions

      const height = Math.ceil(maxHeight * 0.06)
      const top = photo.top + photo.height

      return {
        width: photo.width,
        height,
        left: photo.left,
        top,
        widthRatio: photo.widthRatio,
        heightRatio: height / maxHeight,
        topRatio: top / maxHeight,
        leftRatio: photo.leftRatio
      }
    },

    licenseNumberOverlayDimensions () {
      const { height: maxHeight } = this.cardOverlayDimensions
      const photo = this.photoOverlayDimensions
      const name = this.nameOverlayDimensions

      const top = name.top + name.height + Math.floor(maxHeight * 0.12)

      return {
        width: photo.width,
        height: name.height,
        left: photo.left,
        top,
        widthRatio: name.widthRatio,
        heightRatio: name.heightRatio,
        topRatio: top / maxHeight,
        leftRatio: name.leftRatio
      }
    },

    videoStyles () {
      const { offsetX, offsetY, width, height } = this.videoDimensions

      return width
        ? {
          left: `${offsetX}px`,
          top: `${offsetY}px`,
          width: `${width}px`,
          height: `${height}px`
        }
        : {}
    },

    cardOverlayStyles () {
      const { width, height, offsetX, offsetY } = this.cardOverlayDimensions

      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${offsetX}px`,
        top: `${offsetY}px`
      }
    },

    photoOverlayStyles () {
      const { width, height, left, top } = this.photoOverlayDimensions

      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${left}px`,
        top: `${top}px`
      }
    },

    nameOverlayStyles () {
      const { width, height, left, top } = this.nameOverlayDimensions

      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${left}px`,
        top: `${top}px`
      }
    },

    licenseNumberOverlayStyles () {
      const { width, height, left, top } = this.licenseNumberOverlayDimensions

      return {
        width: `${width}px`,
        height: `${height}px`,
        left: `${left}px`,
        top: `${top}px`
      }
    },

    backdropStyles () {
      const left = `${this.cardOverlayDimensions.offsetX}px`
      const top = `${this.cardOverlayDimensions.offsetY}px`
      const right = `${this.cardOverlayDimensions.offsetX + this.cardOverlayDimensions.width}px`
      const bottom = `${this.cardOverlayDimensions.offsetY + this.cardOverlayDimensions.height}px`

      return {
        'clip-path': `polygon(0% 0%, 0% 100%, ${left} 100%, ${left} ${top}, ${right} ${top}, ${right} ${bottom}, ${left} ${bottom}, ${left} 100%, 100% 100%, 100% 0%)`
      }
    }
  },

  mounted () {
    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.$el)
    this.start()
  },

  unmounted () {
    this.resizeObserver.unobserve(this.$el)
  },

  beforeDestroy () {
    this.close()
  },

  methods: {
    async start () {
      if (!this.$refs.video || this.initializing) return

      this.initializing = true
      await this.updateScreenDimensions()

      try {
        if (!navigator?.mediaDevices?.getUserMedia) {
          this.hasError = true
        } else {
          await this.obtainCameraStream()

          // the FaCardScanner may be destroyed before the camera stream is
          // successfully obtained
          if (!this.$refs.video) return

          this.$refs.video.onloadedmetadata = () => {
            // the FaCardScanner may be destroyed before the video is loaded
            if (!this.$refs.video) return

            this.$refs.video.play()
            this.updateVideoDimensions()
            this.videoReady = true
          }

          this.$refs.video.srcObject = this.cameraStream
        }
      } catch (e) {
        this.hasError = true
        console.error(e)
      } finally {
        this.initializing = false
      }
    },

    async obtainCameraStream () {
      if (this.cameraStream) this.cameraStream.getTracks().forEach(track => track.stop())

      const mediaConstraints = {
        video: this.isIOs
          ? {
            facingMode: 'environment',
            // iOS hack: camera dimensions are flipped
            width: { min: 360, ideal: screen.height, max: 1920 },
            height: { min: 640, ideal: screen.width, max: 1080 }
          }
          : {
            facingMode: 'environment',
            aspectRatio: this.screenDimensions.aspectRatio,
            width: { min: 360, ideal: this.screenDimensions.width, max: 1080 },
            height: { min: 640, ideal: this.screenDimensions.height, max: 1920 }
          }
      }

      this.cameraStream = await navigator.mediaDevices.getUserMedia(mediaConstraints)
      this.cameraSettings = this.cameraStream.getVideoTracks()[0].getSettings()
    },

    async reset () {
      this.previewImg = null

      // without the timeout, iOS devices may not be able to obtain the new
      // camera stream successfully
      await new Promise((resolve) => {
        setTimeout(async () => {
          await this.start()
          resolve()
        }, 250)
      })
    },

    async onResize () {
      if (!this.previewImg) {
        this.stop()

        // without the timeout, on iOS devices the wrong screen size will be
        // used to for camera constraints, which may result in the camera stream
        // dimensions being different from the device orientation
        await new Promise((resolve) => {
          setTimeout(async () => {
            await this.start()
            resolve()
          }, 500)
        })
      }
    },

    /**
     * Computes the desired video element dimensions base on the obtained
     * camera settings. Note that on some devices the camera dimensions may
     * be aligned to the device orientation, but on others they may be
     * different.
     */
    updateVideoDimensions () {
      const { video } = this.$refs

      if (!video) {
        this.videoDimensions = null
      } else {
        const { width: screenWidth, height: screenHeight } = this.screenDimensions

        // iOS hack: flip the camera dimensions
        const cameraActualWidth = this.isIOs ? this.cameraSettings.height : this.cameraSettings.width
        const cameraActualHeight = this.isIOs ? this.cameraSettings.width : this.cameraSettings.height
        const cameraIsPortrait = cameraActualWidth < cameraActualHeight

        let width, height, scaleFactor

        if (cameraIsPortrait) {
          height = screenHeight
          scaleFactor = height / cameraActualHeight
          width = Math.floor(cameraActualWidth * scaleFactor)
        } else {
          width = screenWidth
          scaleFactor = width / cameraActualWidth
          height = Math.floor(cameraActualHeight * scaleFactor)
        }

        const offsetX = (screenWidth - width) / 2
        const offsetY = (screenHeight - height) / 2

        this.videoDimensions = { width, height, offsetX, offsetY, scaleFactor }
      }
    },

    async updateScreenDimensions () {
      return new Promise((resolve) => {
        setTimeout(() => {
          const [width, height] = [window.innerWidth, window.innerHeight]
          this.screenDimensions = {
            width,
            height,
            aspectRatio: width / height,
            isPortrait: width < height
          }

          resolve(this.screenDimensions)
        })
      })
    },

    async capturePreview () {
      // sometimes a single click fires more than one event
      if (!this.videoReady || !this.$refs.video) return

      const card = this.cardOverlayDimensions
      const videoScaleFactor = this.videoDimensions.scaleFactor
      const offsetX = (card.offsetX - this.videoDimensions.offsetX) / videoScaleFactor
      const offsetY = (card.offsetY - this.videoDimensions.offsetY) / videoScaleFactor
      const offsetWidth = this.previewImgWidth = card.width / videoScaleFactor
      const offsetHeight = this.previewImgHeight = card.height / videoScaleFactor

      const ctx = this.$refs.previewCanvas.getContext('2d')

      ctx.drawImage(this.$refs.video, offsetX, offsetY, offsetWidth, offsetHeight, 0, 0, card.width, card.height)

      this.previewImg = this.$refs.previewCanvas.toDataURL('image/jpeg')
      this.stop()
    },

    async acceptPreview () {
      const file = await new Promise((resolve) => { this.$refs.previewCanvas.toBlob(resolve) })
      const [width, height] = [this.previewImgWidth, this.previewImgHeight]
      const { scaleFactor } = this.videoDimensions

      const itemWidth = Math.ceil(width * this.nameOverlayDimensions.widthRatio * scaleFactor)
      const itemLeft = Math.floor(width * this.nameOverlayDimensions.leftRatio * scaleFactor)
      const nameRect = {
        top: Math.floor(height * this.nameOverlayDimensions.topRatio * scaleFactor),
        left: itemLeft,
        width: itemWidth,
        height: Math.ceil(height * this.nameOverlayDimensions.heightRatio * scaleFactor)
      }
      const licenseNumberRect = {
        top: Math.floor(height * this.licenseNumberOverlayDimensions.topRatio * scaleFactor),
        left: itemLeft,
        width: itemWidth,
        height: Math.ceil(height * this.licenseNumberOverlayDimensions.heightRatio * scaleFactor)
      }

      this.$emit('captured', { file, width, height, nameRect, licenseNumberRect })
      this.close()
    },

    onPreviewCanvasResize () {
      this.$refs.previewCanvas.getContext('2d').putImageData(this.previewImage, 0, 0)
    },

    close () {
      this.stop()
      this.$emit('close')
    },

    stop () {
      this.videoReady = false
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop())
      }
    }
  }
}
</script>

<style scoped>
  #fa-card-scanner {
    display: flex;
    position: relative;
    width: 100dvw;
    height: 100dvh;
    background-color: black;
    overflow: hidden;
    touch-action: none;
    user-select: none;

    flex-direction: column;
    justify-content: center;
  }

  video {
    position: absolute;
  }

  #canvas-preview {
    display: none;
  }

  #backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, .5);
    z-index: 1;
  }

  #card-overlay {
    display: none;
    position: absolute;
    z-index: 1;
  }

  .video-ready #card-overlay {
    display: block;
  }

  .photo-overlay {
    position: absolute;
    border: 1px dotted rgba(0, 0, 255, .75);
    overflow: hidden;
  }

  .photo-overlay img {
    position: absolute;
    top: 20%;
    width: 100%;
    height: 100%;
  }

  .name-overlay, .license-number-overlay {
    position: absolute;
    background-color: rgba(0, 0, 255, 0.2);
    border: 1px dashed rgba(0, 0, 255, 1);
  }

  .overlay-hint {
    position: absolute;
    line-height: 1;
    font-size: smaller;
    text-align: center;
    color: rgba(0, 0, 255, 1);
    text-shadow: 0 0 1px rgba(255, 255, 255, .75);
  }

  .overlay-hint.below {
    top: 100%;
    left: 0;
  }

  .overlay-hint.above {
    top: 0;
    right: 0;
    transform: translateY(-100%);
  }

  .hint {
    position: absolute;
    bottom: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, .5);
    padding: 1rem .5rem;
    z-index: 1;
    font-size: smaller;
  }

  .hint p {
    margin: 0 auto;
    text-align: center;
  }

  @media (min-width: 768px) {
    .hint {
      padding: 2rem 0;
      font-size: larger;
    }
  }

  #img-preview {
    height: 60dvh;
    margin: 0 auto;
  }

  .btn-group {
    display: flex;
    width: 100%;
    justify-content: center;
  }

  .btn-group .v-btn {
    width: 33%;
  }

  .btn-group .v-btn .v-icon {
    margin-right: .5rem;
  }

  #error {
    margin: 0 5rem;
    text-align: center;
  }

  .btn-close-float {
    display: none;
    position: absolute;
    top: 2rem;
    left: 2rem;
    background-color: rgba(255, 255, 255, .5);
    z-index: 2;
  }

  .video-ready .btn-close-float {
    display: block;
  }
</style>
